import { useCallback, useEffect, useState } from "react";
import { creditsApi } from "@/lib/api-client";
import { Coins, Loader2, RotateCcw } from "lucide-react";

const CHANNEL_COLORS = {
  oracle: "bg-amber-400",
  aide: "bg-sky-400",
  rfq_escrow: "bg-emerald-400",
  daemon: "bg-white/50",
};

const ESCROW_BADGE = {
  held: "border-amber-500/40 text-amber-400",
  settled: "border-emerald-500/40 text-emerald-400",
  released: "border-rose-500/40 text-rose-300",
};

export const CreditLedgerPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await creditsApi.ledger()); } catch { /* panel stays in standby */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = Object.entries(data?.totals || {});
  const grand = totals.reduce((n, [, t]) => n + (t.est_credits || 0), 0);

  return (
    <section className="uc-card p-5" data-testid="credit-ledger-panel">
      <div className="flex items-center gap-2 mb-3">
        <Coins size={13} className="text-amber-400" />
        <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">
          Sovereign Credit Ledger · Universal Key spend
        </span>
        <span className="font-mono-uc text-[9px] text-white/30">estimated</span>
        {data?.escrow_held?.entries > 0 && (
          <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400"
                data-testid="credit-escrow-held">
            Escrow held ~{data.escrow_held.est_credits.toFixed(2)}cr
          </span>
        )}
        <button data-testid="credit-ledger-refresh" onClick={load} disabled={loading}
                className="ml-auto text-white/40 hover:text-amber-400 transition-colors disabled:opacity-40"
                title="Refresh ledger">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
        </button>
      </div>
      {!loading && !totals.length && (
        <div className="font-mono-uc text-[10px] text-white/35" data-testid="credit-ledger-empty">
          No agent spend recorded yet — Oracle and Aide sessions file here automatically.
        </div>
      )}
      {totals.length > 0 && (
        <>
          <div className="space-y-2 mb-3" data-testid="credit-ledger-totals">
            {totals.sort((a, b) => b[1].est_credits - a[1].est_credits).map(([ch, t]) => (
              <div key={ch} className="flex items-center gap-3" data-testid={`credit-channel-${ch}`}>
                <span className="font-mono-uc text-[9px] uppercase tracking-widest text-white/60 w-16">{ch}</span>
                <div className="flex-1 h-1.5 bg-white/[0.06]">
                  <div className={`h-full ${CHANNEL_COLORS[ch] || "bg-white/30"}`}
                       style={{ width: `${grand ? Math.max(4, (t.est_credits / grand) * 100) : 0}%` }} />
                </div>
                <span className="font-mono-uc text-[10px] text-white/70 w-20 text-right">
                  ~{t.est_credits.toFixed(2)}cr
                </span>
                <span className="font-mono-uc text-[9px] text-white/35 w-16 text-right">{t.entries} calls</span>
              </div>
            ))}
          </div>
          <div className="space-y-1 border-t border-white/5 pt-2" data-testid="credit-ledger-entries">
            {(data?.entries || []).slice(0, 5).map((e) => (
              <div key={e.id} className="flex items-center gap-2 font-mono-uc text-[9px] text-white/40">
                <span className={`uc-dot ${CHANNEL_COLORS[e.channel] || "bg-white/25"}`}></span>
                <span className="uppercase tracking-widest text-white/55 w-12">{e.channel}</span>
                <span className="truncate flex-1">{e.note}</span>
                {e.status && (
                  <span className={`text-[8px] uppercase tracking-widest px-1 py-0.5 border ${ESCROW_BADGE[e.status] || "border-white/15"}`}>
                    {e.status}
                  </span>
                )}
                <span className="text-white/60">~{e.est_credits}cr</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default CreditLedgerPanel;
